import { useState } from 'react';
import Button from '@/components/ui/Button';

// Scenario definitions by industry and type
const SCENARIOS = {
  general: [
    { id: 'c1', label: 'Cold Call Prospect', description: 'Prospect who has not heard of you before, somewhat skeptical' },
    { id: 'c2', label: 'Warm Lead', description: 'Someone referred by a colleague, interested but evaluating options' },
    { id: 'c3', label: 'Existing Customer', description: 'Current customer exploring additional services or upgrades' },
    { id: 'c4', label: 'Budget-Conscious Buyer', description: 'Decision maker concerned about ROI and total cost of ownership' },
    { id: 'c5', label: 'Technical Evaluator', description: 'Technical person focused on features, integrations, and compatibility' }
  ],
  saas: [
    { id: 's1', label: 'VP of Sales at Growing Startup', description: 'Fast-growing SaaS startup (50-200 people), frustrated with current tools, open but skeptical' },
    { id: 's2', label: 'Operations Manager at Enterprise', description: 'Large enterprise (1000+), process-driven, needs long implementation timeline' },
    { id: 's3', label: 'CFO Focused on ROI', description: 'Financial decision maker at mid-market SaaS, wants clear revenue impact metrics' },
    { id: 's4', label: 'Tech Stack Manager', description: 'CTO/Technical lead evaluating integration with existing platforms' },
    { id: 's5', label: 'Marketing Director', description: 'Marketing executive looking for solution to improve lead quality and conversion' }
  ],
  financial: [
    { id: 'f1', label: 'Bank Branch Manager', description: 'Regional bank manager managing team, concerned about compliance and risk' },
    { id: 'f2', label: 'Investment Advisor', description: 'Financial advisor focused on client experience and advisory tools' },
    { id: 'f3', label: 'Risk Officer', description: 'Risk management professional prioritizing security and regulations' },
    { id: 'f4', label: 'Fintech Founder', description: 'Startup founder building financial product, cost-conscious and growth-focused' },
    { id: 'f5', label: 'Wealth Manager', description: 'Private wealth manager managing high-net-worth clients, service-focused' }
  ],
  healthcare: [
    { id: 'h1', label: 'Practice Manager', description: 'Medical practice manager handling operations and staff, efficiency-focused' },
    { id: 'h2', label: 'Healthcare IT Director', description: 'Director of IT managing EHR and patient data systems' },
    { id: 'h3', label: 'Hospital Administrator', description: 'Hospital administrator dealing with budgets and multi-department coordination' },
    { id: 'h4', label: 'Clinic Director', description: 'Small clinic director wearing multiple hats, budget-conscious' },
    { id: 'h5', label: 'Chief Medical Officer', description: 'CMO focused on clinical outcomes and patient safety' }
  ],
  retail: [
    { id: 'r1', label: 'Store Manager', description: 'Retail store manager managing inventory, staff, and sales targets' },
    { id: 'r2', label: 'E-commerce Manager', description: 'Online retail manager focused on conversion and customer experience' },
    { id: 'r3', label: 'Category Manager', description: 'Product category manager optimizing margins and product mix' },
    { id: 'r4', label: 'Regional VP', description: 'Regional executive overseeing multiple locations, strategic focus' },
    { id: 'r5', label: 'Customer Service Director', description: 'Director improving customer satisfaction and operational efficiency' }
  ],
  manufacturing: [
    { id: 'm1', label: 'Plant Manager', description: 'Plant manager focused on production efficiency and downtime reduction' },
    { id: 'm2', label: 'Supply Chain Manager', description: 'Supply chain professional optimizing logistics and costs' },
    { id: 'm3', label: 'Quality Manager', description: 'Quality assurance manager ensuring standards and compliance' },
    { id: 'm4', label: 'Operations Director', description: 'Operations director managing multiple facilities and processes' },
    { id: 'm5', label: 'CFO/Finance Lead', description: 'Finance leader focused on cost reduction and operational metrics' }
  ]
};

export default function RolePlaySetup({ onStartSession, isLoading, industry = 'saas' }) {
  const [practiceType, setPracticeType] = useState('general');
  const [difficultyLevel, setDifficultyLevel] = useState('beginner');
  const [salesStage, setSalesStage] = useState('discovery');
  const [scenarioId, setScenarioId] = useState('');
  const [error, setError] = useState('');

  // Get scenarios for current industry + general scenarios
  const availableScenarios = [
    ...SCENARIOS.general,
    ...(SCENARIOS[industry] || SCENARIOS.saas)
  ];

  const selectedScenario = availableScenarios.find(s => s.id === scenarioId);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!scenarioId) {
      setError('Please select a scenario');
      return;
    }

    onStartSession({
      practiceType,
      difficultyLevel,
      industry,
      salesStage: practiceType === 'phone_sequence' ? 'phone_call' : salesStage,
      scenarioBriefing: selectedScenario?.label || ''
    });
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Practice Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Practice Type
          </label>
          <div className="space-y-3">
            <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50" style={{ borderColor: practiceType === 'general' ? '#3b82f6' : '' }}>
              <input
                type="radio"
                name="practiceType"
                value="general"
                checked={practiceType === 'general'}
                onChange={(e) => setPracticeType(e.target.value)}
                disabled={isLoading}
                className="mr-3"
              />
              <div>
                <p className="font-medium text-gray-900">General Roleplay</p>
                <p className="text-sm text-gray-600">Free-form practice on any sales stage</p>
              </div>
            </label>
            <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50" style={{ borderColor: practiceType === 'phone_sequence' ? '#3b82f6' : '' }}>
              <input
                type="radio"
                name="practiceType"
                value="phone_sequence"
                checked={practiceType === 'phone_sequence'}
                onChange={(e) => setPracticeType(e.target.value)}
                disabled={isLoading}
                className="mr-3"
              />
              <div>
                <p className="font-medium text-gray-900">📞 Phone Call Sequence</p>
                <p className="text-sm text-gray-600">Guided 6-stage phone call following foundational scripts</p>
              </div>
            </label>
          </div>
        </div>

        {/* Difficulty Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Difficulty Level
          </label>
          <select
            value={difficultyLevel}
            onChange={(e) => setDifficultyLevel(e.target.value)}
            disabled={isLoading}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
          >
            <option value="beginner">Beginner - Cooperative prospect, basic objections</option>
            <option value="intermediate">Intermediate - Moderate resistance, nuanced objections</option>
            <option value="advanced">Advanced - Challenging prospect, sophisticated objections</option>
            <option value="expert">Expert - Highly resistant, complex multi-objection scenarios</option>
          </select>
        </div>

        {/* Sales Stage - Hidden for Phone Sequence */}
        {practiceType !== 'phone_sequence' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sales Stage
            </label>
            <select
              value={salesStage}
              onChange={(e) => setSalesStage(e.target.value)}
              disabled={isLoading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            >
              <option value="discovery">Discovery - Initial conversation, needs analysis</option>
              <option value="presentation">Presentation - Solution presentation and benefits</option>
              <option value="objection">Objection Handling - Addressing concerns and roadblocks</option>
              <option value="closing">Closing - Moving to commitment and next steps</option>
            </select>
          </div>
        )}

        {/* Scenario Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Choose Your Scenario
          </label>
          <select
            value={scenarioId}
            onChange={(e) => setScenarioId(e.target.value)}
            disabled={isLoading}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
          >
            <option value="">Select a scenario...</option>
            {availableScenarios.map((scenario) => (
              <option key={scenario.id} value={scenario.id}>
                {scenario.label}
              </option>
            ))}
          </select>
          
          {/* Scenario Description */}
          {selectedScenario && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">{selectedScenario.label}</span>
                <br />
                <span className="text-gray-600">{selectedScenario.description}</span>
              </p>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isLoading || !scenarioId}
          className="w-full"
        >
          {isLoading ? 'Starting Practice Session...' : 'Start Practice Session'}
        </Button>
      </form>

      {/* Tips */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Tips for best results:</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          {practiceType === 'phone_sequence' ? (
            <>
              <li>• Follow the 6-stage phone sequence in order</li>
              <li>• Each stage builds on the previous one</li>
              <li>• You'll get real-time coaching at each step</li>
              <li>• Focus on the foundational techniques provided</li>
            </>
          ) : (
            <>
              <li>• Start with Beginner if new to the framework</li>
              <li>• Use Discovery to practice qualification and needs analysis</li>
              <li>• Set specific prospect personas to make it realistic</li>
              <li>• Focus on one technique per session</li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
}
